import React, { useContext } from 'react';
import Button from '../../common/Button';
import { Input } from '../../common/form/Input';
import { Phone } from '../../common/form/Phone';
import SingleImageUploader from '../../common/SingleImageUploader';
import Modal, { ModalSizes } from '../../common/Modal';
import { OpenClosedStates } from '../../common/OpenClosedStates';
import { useForm } from '../../common/hooks/useForm';
import { ToastContext, ToastTypes } from '../../common/contexts/toast.context';
import { isValidUrl } from '../../helpers';
import { isValid } from '../../common/helpers/isValid';

export type DisplayAdForm = {
	id: number;
	firstName: string;
	lastName: string;
	company: string;
	contactEmail: string;
	phone: string;
	urlSmall: string;
	urlLarge: string;
	link: string;
};

type Props = {
	request: Partial<DisplayAdForm>;
	state: OpenClosedStates;
	close: () => void;
	onUpdate: (updatedRequest: Partial<DisplayAdForm>) => Promise<void>;
};

const validation = {
	firstName: {
		required: { message: 'First name is required.' },
	},
	lastName: {
		required: { message: 'Last name is required.' },
	},
	company: {
		required: { message: 'Company name is required.' },
	},
	contactEmail: {
		required: { message: 'Contact email is required.' },
	},
	link: {
		function: {
			value: (val: Partial<DisplayAdForm>) => {
				if (!isValidUrl(val.link)) {
					const hint = isValid(val.link)
						? 'and the format should be https://' + val.link?.replace('http://', '')
						: '';
					return 'A valid destination URL is required ' + hint;
				}
				return false;
			},
		},
	},
	urlSmall: {
		function: {
			value: (val: Partial<DisplayAdForm>) => {
				if (!val.urlSmall && !val.urlLarge) {
					return 'At least one ad image (small or large) is required.';
				}
				return false;
			},
		},
	},
};

const DisplayAdDetailsModal = ({ request, state, close, onUpdate }: Props) => {
	const {
		value,
		registerSubmit,
		onChange,
		errors,
		isDirty,
		isSubmitting,
	} = useForm<Partial<DisplayAdForm>>(request, validation);

	const { createToast } = useContext(ToastContext);

	const onSubmit = async (val: Partial<DisplayAdForm>) => {
		await onUpdate(val);
	};

	const onSuccess = async () => {
		close();
		createToast({
			title: 'Success!',
			description: <>Successfully updated display ad.</>,
			type: ToastTypes.Success,
			duration: 5000,
		});
	};

	const onFail = async () => {
		createToast({
			title: 'Failed!',
			description: <>There was an issue updating the display ad. Please try again.</>,
			type: ToastTypes.Fail,
			duration: 5000,
		});
	};

	return (
		<Modal state={state} size={ModalSizes.full} minHeight={500} close={close}>
			<form onSubmit={registerSubmit(onSubmit, { onSuccess, onFail })}>
				<div className="px-6 pt-6 bg-gray-50 h-full">
					<div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
						<Input
							name="firstName"
							label="First name *"
							value={value.firstName}
							onChange={onChange('firstName')}
							className="col-span-4 sm:col-span-2"
							errors={errors.fieldErrors?.firstName}
						/>
						<Input
							name="lastName"
							label="Last name *"
							value={value.lastName}
							onChange={onChange('lastName')}
							className="col-span-4 sm:col-span-2"
							errors={errors.fieldErrors?.lastName}
						/>
						<Input
							name="company"
							label="Company name *"
							value={value.company}
							onChange={onChange('company')}
							className="col-span-4 sm:col-span-2"
							errors={errors.fieldErrors?.company}
						/>
						<Input
							name="contactEmail"
							label="Contact email *"
							value={value.contactEmail}
							onChange={onChange('contactEmail')}
							className="col-span-4 sm:col-span-2"
							errors={errors.fieldErrors?.contactEmail}
						/>
						<Phone
							name="phone"
							label="Contact phone"
							value={value.phone}
							onChange={onChange('phone')}
							className="col-span-4 sm:col-span-2"
							errors={errors.fieldErrors?.phone}
						/>
						<Input
							name="link"
							label="Destination URL *"
							value={value.link}
							onChange={onChange('link')}
							className="col-span-4 sm:col-span-6"
							errors={errors.fieldErrors?.link}
						/>
						<div className="col-span-6">
							<p className="text-sm text-gray-600 mb-3">
								Ad images — at least one required.
							</p>
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
								<div>
									<SingleImageUploader
										name="urlSmall"
										label="Small — desktop (300px × 150px)"
										value={value.urlSmall}
										onChange={onChange('urlSmall')}
										className="w-full"
									/>
								</div>
								<div>
									<SingleImageUploader
										name="urlLarge"
										label="Large — mobile (728px × 90px)"
										value={value.urlLarge}
										onChange={onChange('urlLarge')}
										className="w-full"
									/>
								</div>
							</div>
							{!value.urlSmall && !value.urlLarge && errors?.fieldErrors?.urlSmall && (
								<div className="text-red-500 mt-2 text-sm">
									{errors.fieldErrors.urlSmall.message}
								</div>
							)}
						</div>
					</div>
				</div>
				<div className="col-span-6 flex justify-end p-4">
					<Button
						text="Close"
						loading={isSubmitting}
						onClick={() => close()}
					/>
					<span className="mr-5"></span>
					<Button
						text="Update"
						type="submit"
						loading={isSubmitting}
						disabled={!isDirty}
						onClick={(e) => e.stopPropagation()}
					/>
				</div>
			</form>
		</Modal>
	);
};

export default DisplayAdDetailsModal;
